import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash-es';
import { ClickAwayListener, Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import MenuIconOpen from '@mui/icons-material/MenuOpen';
import { useAuth, useRouter } from 'AurionCR/components';
import LogoIcon from 'images/logo_icon.svg?react';
import LogoTitle from 'images/logo_title.svg?react';
import { MainNav, OnMenuOpen } from './navigations';
import { ContentLayout } from './content-layout';
import { MenuLanguages } from './navigations/menu-languages';
import { LayoutShell, LayoutShellProvider } from './shell-context';

import './_style.scss';
import { PoweredBy } from 'components/layout/navigations/powered-by';
import { useTranslate } from 'hooks/use-translate';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { useSmallDialogSheets } from 'hooks/use-small-dialog-sheets';

/** The temporary drawer portals into `#root` so the shell's `#root .root-drawer` styles reach it. */
const rootContainer = () => document.getElementById('root');

/**
 * The app shell. Above `md` the navigation is the permanent drawer it always
 * was (collapsed / expanded, persisted per user). Below `md` (Phase F1,
 * Task 5c, D-55) it is MUI's temporary variant: closed by default, opened by
 * the header's hamburger through `LayoutShellContext`, closed on navigation,
 * Escape and the backdrop; MUI mirrors the anchor under an RTL theme.
 */
const Layout = memo(({ children }: React.PropsWithChildren) => {
  const { t } = useTranslate();
  const { user } = useAuth();
  const router = useRouter();
  const appUserID = user ? user.appUserID : null;
  const isBelowMd = useIsBelowMd();
  // N2c (T4, D-126): below md a small dialog is a bottom sheet
  useSmallDialogSheets(isBelowMd);

  // state
  const [isInit, setIsInit] = useState(false);
  const [open, setOpen] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // handlers
  const onMenu = useCallback(
    (bool: boolean) => {
      setOpen(bool);
      if (appUserID) localStorage.setItem(`menu_user-${appUserID}`, JSON.stringify({ open: bool }));
    },
    [setOpen, appUserID],
  );
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const onMenuOpen: OnMenuOpen = ({ ref, route, level }) => {
    if (isBelowMd) {
      // A leaf (or a level-2 entry) navigates: the temporary drawer closes.
      if (!route.routes || level === 2) closeMenu();
      return;
    }
    if (route.routes) {
      if (!open) {
        debounce(() => ref?.current?.setOpen(true), 50)();
      }
      setOpen(true);
    }
    if (!route.routes || level === 2) {
      setOpen(false);
    }
  };
  // init
  useEffect(() => {
    if (appUserID) {
      const store_ = JSON.parse(localStorage.getItem(`menu_user-${appUserID}`) || '{}');
      setOpen(store_ && store_.open !== undefined ? store_.open : true);
      setIsInit(true);
    }
  }, [setOpen, appUserID]);
  // close on navigate
  useEffect(() => {
    setIsMenuOpen(false);
  }, [router.pathname]);

  const shell = useMemo<LayoutShell>(
    () => ({ isBelowMd, isMenuOpen, openMenu, closeMenu }),
    [isBelowMd, isMenuOpen, openMenu, closeMenu],
  );

  // N1 / T2: below md the drawer's header is the logo and a close button at its
  // end edge; the desktop's rail toggle (open / collapse) has no meaning there.
  const drawerContent = (
    <>
      <div className="burger">
        {!isBelowMd && (
          <span className="icon" onClick={() => onMenu(!open)}>
            {open ? <MenuIconOpen /> : <MenuIcon />}
          </span>
        )}
        <div className="logo-box">
          <div className="logo">
            <LogoIcon />
          </div>
          <div className="description">
            <LogoTitle />
            <div className="slogan">{t('login-slogan')}</div>
          </div>
        </div>
        {isBelowMd && (
          <IconButton className="burger__close" aria-label={t('close')} onClick={closeMenu}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
      <MainNav collapsed={isBelowMd ? false : !open} onMenuOpen={onMenuOpen} accordion={true} />
      <PoweredBy>
        <MenuLanguages />
      </PoweredBy>
    </>
  );
  // render
  return (
    <LayoutShellProvider value={shell}>
      <div className="root-content">
        {isInit &&
          (isBelowMd ? (
            <Drawer
              className="root-drawer drawer hover root-drawer--temporary"
              variant="temporary"
              open={isMenuOpen}
              onClose={closeMenu}
              ModalProps={{ container: rootContainer, keepMounted: false }}
            >
              {drawerContent}
            </Drawer>
          ) : (
            <ClickAwayListener onClickAway={() => onMenu(false)}>
              <Drawer className={`root-drawer drawer ${open ? 'hover' : ''}`} variant="permanent">
                {drawerContent}
              </Drawer>
            </ClickAwayListener>
          ))}
        <ContentLayout>{children}</ContentLayout>
      </div>
    </LayoutShellProvider>
  );
});

export default Layout;
